import React, {useEffect, useRef, useState} from 'react';
import {GiCheckMark, GiCrossMark} from "react-icons/gi";

/**
 * A 6-digit code input component for verification codes, MFA, etc.
 * @param {Function} onComplete - Callback function called when all 6 digits are entered
 * @param {Function} onChange - Callback function called on every change
 * @param {boolean} disabled - Whether the input is disabled
 * @param {boolean} showValidityIcon - Show an indicator whether the input is valid
 * @param {boolean} isValid -
 */
export default function SixDigitCode({onComplete, onChange, disabled = false, showValidityIcon = false, isValid = false}) {
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const inputRefs = useRef([]);

    useEffect(() => {
        setTimeout(() => {
            // Focus on the first input after the component mounts
            // Does not work without the timeout
            inputRefs.current[0]?.focus();
        }, 1000);
    }, []);

    const handleChange = (index, value) => {
        // Only allow digits
        if (value && !/^\d$/.test(value)) {
            return;
        }

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        // Call onChange callback if provided
        if (onChange) {
            onChange(newCode.join(''));
        }

        // Move to next input if value was entered
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        // Call onComplete callback if all digits are entered
        if (newCode.every(digit => digit !== '') && onComplete) {
            onComplete(newCode.join(''));
        }
    };

    const handleKeyDown = (index, e) => {
        // Move to previous input on backspace if current input is empty
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').trim();

        // Only process if pasted data is exactly 6 digits
        if (/^\d{6}$/.test(pastedData)) {
            const newCode = pastedData.split('');
            setCode(newCode);

            if (onChange) {
                onChange(pastedData);
            }

            // Focus on the last input
            inputRefs.current[5]?.focus();

            // Call onComplete callback
            if (onComplete) {
                onComplete(pastedData);
            }
        }
    };

    return (
        <div style={{display: 'flex', gap: '10px', justifyContent: 'center'}}>
            {code.map((digit, index) => (
                <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    className="form-control digit-input"
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    disabled={disabled}
                    autoComplete="off"
                />
            ))}
            {showValidityIcon && <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                {isValid ? <GiCheckMark color={'#59C386'} size={'1.8em'}/> : <GiCrossMark color={'#c04040'} size={'1.8em'}/>}
            </div>}
        </div>
    );
}
